import '@/register-store'
import '@/utils/polyfill/intl'
import { Sentry } from '@/utils/error-handling'

Sentry.init()

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouteMatch, RouterProvider, createRouter } from '@tanstack/react-router'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'

import '@/index.css'

import Axios from 'axios'
import React, { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { BehaviorSubject } from 'rxjs'
import { toast } from 'sonner'

import { Api } from '@/api/api'
import { ApiProvider } from '@/api/react-query'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import RootProviders from '@/components/root-providers'
import { AuthProvider, authToken } from '@/hooks/use-auth'
import { useI18n } from '@/i18n/i18n-react'
import { Translations } from '@/i18n/i18n-types'
import { RouterContextType } from '@/routes/__root'
// Import the generated route tree
import { routeTree } from '@/routeTree.gen'
import { getRandomId } from '@/utils/utilities'

window.__DASHBOARD_VERSION__ = __DASHBOARD_VERSION__

window.addEventListener('vite:preloadError', event => {
	event.preventDefault()
	const key = 'vite-preload-reload'
	if (sessionStorage.getItem(key)) return
	sessionStorage.setItem(key, '1')
	window.location.reload()
})

const isTokenExpired = (token: string): boolean => {
	try {
		const payload = JSON.parse(atob(token.split('.')[1]))
		return payload.exp * 1000 < Date.now()
	} catch {
		return true
	}
}

const getBackendUrl = () => {
	if (__BACKEND_URL__) {
		return __BACKEND_URL__
	}

	if (process.env.NODE_ENV === 'production') {
		const hostname = window.location.hostname

		if (!hostname) {
			return '/'
		}

		// Temporary while we roll the new one out
		const splits = hostname.split('.')
		splits.unshift('api')
		return `https://${splits.join('.')}/`
	}

	return '/'
}

class FastTrackApi extends Api {
	private initializing = new BehaviorSubject(false)
	private initialized = new BehaviorSubject(false)
	private hasShownSessionExpired = false

	constructor() {
		const client = Axios.create({
			baseURL: getBackendUrl()
		})

		client.interceptors.request.use(
			request => {
				request.headers['request-id'] = getRandomId()
				if (authToken.value) {
					request.headers['Authorization'] = `Bearer ${authToken.value}`
				}
				return request
			},
			error => {
				return Promise.reject(error)
			}
		)

		client.interceptors.response.use(
			response => response, // Directly return successful responses.
			async error => {
				const originalRequest = error.config
				if (originalRequest?.url?.includes('/login')) {
					return Promise.reject(error)
				}

				if (
					Axios.isAxiosError(error) &&
					error.response?.status === 401 &&
					originalRequest &&
					!originalRequest?._retry
				) {
					const headers = originalRequest.headers
					const authHeader =
						headers &&
						(typeof (headers as { get?: (n: string) => string | undefined }).get === 'function'
							? (headers.get?.('Authorization') ?? headers.get?.('authorization'))
							: ((headers as { Authorization?: string; authorization?: string }).Authorization ??
								(headers as { authorization?: string }).authorization))
					// In-flight requests that had no auth (e.g. prefetch) must not clear a token set
					// by login that completed before this response arrived.
					if (!authHeader) {
						return Promise.reject(error)
					}

					originalRequest._retry = true

					// Clear auth token
					authToken.next('')

					// Show toast notification only once per session
					if (!this.hasShownSessionExpired) {
						this.hasShownSessionExpired = true
						toast.error('Your session has expired. Please log in again.')
					}

					// Clear query cache to remove stale authenticated data
					if (queryClient) {
						queryClient.clear()
					}

					// Navigate to login with redirect
					if (router) {
						const currentPath = window.location.pathname
						router.navigate({
							to: '/login',
							search: {
								redirect: currentPath === '/' ? undefined : currentPath
							}
						})
					}
				}
				return Promise.reject(error) // For all other errors, return the error as is.
			}
		)

		super(client)
	}

	resetSessionExpiredFlag() {
		this.hasShownSessionExpired = false
	}
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			staleTime: 90000,
			// Don't retry on 401 -- the response interceptor handles
			// token clearing and redirect to login.
			retry: (failureCount, error) => {
				if (Axios.isAxiosError(error) && [401, 403, 404].includes(error.response?.status ?? 0)) {
					return false
				}
				return failureCount < 1
			}
		}
	}
})

// Create a new router instance (needs to be created before api so we can reference it)
let router: ReturnType<typeof createRouter>
router = createRouter({
	routeTree,
	defaultPreload: 'intent',
	defaultPendingComponent: () => <LoadingSpinner centered />,
	context: { queryClient, api: undefined as any, L: undefined as unknown as Translations }
})

// Create api instance after router is available
export const api = new FastTrackApi()

// Update router context with api
router.update({
	context: { queryClient, api, L: undefined as unknown as Translations }
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule])

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement)
	root.render(
		<StrictMode>
			<AuthProvider>
				<ApiProvider api={api}>
					<QueryClientProvider client={queryClient}>
						<RootProviders>
							<RouterProviderWithContext />
							{/* <Suspense>
								<TanStackRouterDevtools />
							</Suspense> */}
							{/* <ReactQueryDevtools initialIsOpen={false} /> */}
						</RootProviders>
					</QueryClientProvider>
				</ApiProvider>
			</AuthProvider>
		</StrictMode>
	)
}

declare module '@tanstack/react-router' {
	interface StaticDataRouteOption {
		crumb?: (args: RouteMatch<any, any, any, any, any, RouterContextType, any>) => string
	}
}

function RouterProviderWithContext() {
	const { L } = useI18n()
	return <RouterProvider router={router} context={{ queryClient, api, L }} />
}
